<template>
  <div class="annset-li">
    <div class="annset-name-desc">
      <div class="annset-name">
        <span class="set-name">{{ isDebugMode ? `(${annotationSet.id}) ${annotationSet.name}` : `${annotationSet.name}` }}
        </span>
        <EncodedIdField
          :id="annotationSet.id"
          class="ml-3 mr-3"
          :type="'annotation set'"
          :hideLabel="true"
        />
      </div>
      <div class="annset-desc">
        <SVGIcon
          :iconName="'image'"
          :width="'18px'"
          :height="'18px'"
        />
        <span>{{ imgCount }}</span>
        <SVGIcon
          :iconName="'tag'"
          :width="'18px'"
          :height="'18px'"
        />
        <span>{{ annotationCount }}</span>
        <template v-if="loadingHash || hashId">
          <SVGIcon
            :iconName="'hash_tag'"
            :width="'18px'"
            :height="'18px'"
          />
          <InlineLoader
            v-if="loadingHash && !hashId"
            :width="'18px'"
            :height="'18px'"
            :border="'2px'"
            class="ml-2"
          />
          <template v-if="hashId">
            <Tooltip :placement="'top-start'" :trigger="'click'">
              <template #body>
                <pre>Full dataset hash copied</pre>
              </template>
              <SVGIcon
                class="icon-copy"
                :iconName="'copy'"
                :width="'16px'"
                :height="'16px'"
                @click.stop="copyToClipboard(hashId)"
              />
            </Tooltip>
            <span>{{ shorten(hashId) }}</span>
          </template>
        </template>
      </div>
    </div>
    <div v-if="canWrite" class="icon-container">
      <BaseMenu
        :placement="'bottom-start'"
        :appendToBody="true"
        @closed="show_menu = false"
      >
        <IconButton
          v-model="show_menu"
          class="edit-set-icon"
          :icon="'more_vert'"
          :width="22"
          :height="22"
        />
        <template #menu="{closeMenu}">
          <ul>
            <li>
              <BaseMenuButton v-tooltip="'Edit annotation set name and description'" @click.stop="$emit('open-description'), closeMenu()">
                <SVGIcon
                  :iconName="'edit'"
                  :width="'22px'"
                  :height="'22px'"
                />
                <span>Edit</span>
              </BaseMenuButton>
            </li>
            <li>
              <BaseMenuButton
                v-tooltip="'Generate hash value from dataset and selected annotation set'"
                :disabled="isFrozen"
                @click.stop="generateHash(), closeMenu()"
              >
                <SVGIcon
                  :iconName="'hash_tag'"
                  :width="'22px'"
                  :height="'22px'"
                />
                <span>Generate Hash</span>
              </BaseMenuButton>
            </li>
            <li v-if="!isFrozen">
              <BaseMenuButton
                v-tooltip="'Move annotation set to recycle bin. Annotation set will be billed at normal rate until it is purged'"
                class="_danger"
                :disabled="isFrozen"
                @click.stop="$emit('delete-annset'), closeMenu()"
              >
                <SVGIcon
                  :iconName="'trash'"
                  :width="'22px'"
                  :height="'22px'"
                />
                <span>Move to Recycle Bin</span>
              </BaseMenuButton>
            </li>
          </ul>
        </template>
      </BaseMenu>
      <!-- <IconButton
        class="edit-set-icon"
        :icon="'conf_matrix'"
        :width="22"
        :height="22"
        :title="`View Confusion Matrix`"
        :type="''"
        @click.stop="$emit('open-conf-matrix')"
      /> -->
    </div>
  </div>
</template>

<script>
import SVGIcon from '@/components/SVGIcon.vue';
import IconButton from '@/components/IconButton.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface.js';
import BaseMenu from '@/components/BaseMenu.vue';
import BaseMenuButton from '@/components/BaseMenuButton.vue';
import EncodedIdField from '@/components/EncodedIdField.vue';
import InlineLoader from '@/components/InlineLoader.vue';
import Tooltip from "@/components/Tooltip.vue";

export default {
  name: "AnnotationSetListItem",
  components: {
    SVGIcon,
    IconButton,
    BaseMenu,
    BaseMenuButton,
    EncodedIdField,
    InlineLoader,
    Tooltip,
  },
  props: {
    annotationSet: {
      type: Object,
      default: () => {},
    },
    numImg: {
      type: Number,
      default: 0,
    },
    numAnnotations: {
      type: Number,
      default: 0,
    },
    refresh: {
      type: Boolean,
      default: false,
    },
    isFrozen: {
      type: Boolean,
      default: false,
    },
    canWrite: {
      type: Boolean,
      default: true,
    },
    hashId: {
      type: String,
      default: null,
    },
  },
  emits: {
    'delete-annset': null, 'copy-annset': null, 'update:numImg': null, 'update:numAnnotations': null, 'open-conf-matrix': null, 'open-description': null, 'generate-hash': null,
  },
  data() {
    return {
      dataConnect: new DatastoreConnect(this.$store.state.enterpriseServerUrl),
      imgTotal: 0,
      loadingHash: false,
      show_menu: false,
    };
  },
  computed: {
    isDebugMode() {
      return this.$store.state.debugMode;
    },
    imgCount: {
      get() { return this.numImg; },
      set(v) { this.$emit('update:numImg', v); },
    },
    annotationCount: {
      get() { return this.numAnnotations; },
      set(v) { this.$emit('update:numAnnotations', v); },
    },
  },
  watch: {
    hashId() {
      this.loadingHash = false;
    },
  },
  methods: {
    shorten(hashId) {
      return hashId.slice(0, 8);
    },
    generateHash() {
      this.loadingHash = true;
      this.$emit('generate-hash');
    },
    copyToClipboard(text) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text);
      }
    },
  },
};
</script>

<style lang="scss" scoped>

.annset-li-container {
  overflow-y: auto;
  font-size: 0.875rem;
  flex: 1 1;
}

.annset-li {
  display: flex;
  padding-left: 10px;
  padding-right: 10px;
  padding-top: 5px;
  padding-bottom: 5px;
  border-radius: 4px;

  .annset-name-desc {
    flex: 1 1 auto;
  }

  .set-name {
    font-weight: 600;
  }

  .annset-name {
    display: flex;
    flex-direction: row;
    align-items: center;
    @include themify() {
      color: themed('body-text-color');
    }
  }

  .annset-desc {
    display: flex;
    align-items: center;
    @include themify() {
      color: themed('body-text-color-secondary');
    }

    span {
      margin-left: 2px;
      font-size: 0.9rem;
    }

    svg:not(:first-of-type) {
      margin-left: 15px;
    }
  }
}

.annset-li:hover {
  background-color: rgb(237, 232, 232);
}

.annset-li .copy-annset-icon,
.annset-li .delete-annset-icon,
.annset-li .edit-set-icon,
.annset-li:hover .copy-annset-icon,
.annset-li:hover .delete-annset-icon,
.annset-li:hover .edit-set-icon{
  z-index: 3;
  visibility: visible;
  @include themify() {
    color: themed('icon-color-inactive');
  }
}

.annset-li:hover .copy-annset-icon:hover,
.annset-li:hover .edit-set-icon:hover {
  @include themify() {
    color: themed('icon-color-primary');
  }
}

.annset-li:hover .delete-annset-icon:hover {
  @include themify() {
    color: themed('color-red');
  }
}

.annset-li + .annset-li {
  margin-top: 2px;
}

.icon-container {
  display: flex;
  align-items: center;
  margin-left: 12px;
  button + button {
    margin-left: 10px;
  }
  @include themify() {
    color: themed('icon-color-inactive');
  }
}

.icon-copy {
  margin: 0 5px !important;
  @include themify() {
    color: themed('icon-color-primary');
  }
  cursor: pointer;
}

</style>
